/**
 * Custom Reports tab — list saved reports, run them, and (managers+) build new ones.
 *
 * The "builder" is a single dialog that walks through:
 *   1. Pick dataset (Tickets / Email log / Audit log)
 *   2. Add filters (AND only — keep MVP simple)
 *   3. Pick group-by dimension(s) + granularity for dates
 *   4. Pick measure (count / avg / etc.)
 *   5. Pick viz (bar / stacked_bar / line / area / pie / table / single_stat)
 *   6. Save (private / dept / org)
 *
 * The "viewer" renders any saved or ad-hoc result using `CustomReportChart`.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Legend,
} from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Sparkles, Plus, Pencil, Trash2, Play, X, Lock, Building2, Globe2, Eye, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import CompareBadge from "@/components/reports/CompareBadge";

const COLORS = ["#4f46e5", "#059669", "#ea580c", "#9333ea", "#0ea5e9", "#e11d48", "#64748b", "#d97706", "#16a34a", "#7c3aed"];
const SCOPE_META = {
  private: { label: "Private", Icon: Lock, tint: "bg-slate-50 text-slate-600 border-slate-200" },
  dept: { label: "Department", Icon: Building2, tint: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  org: { label: "Organization", Icon: Globe2, tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const blank = () => ({
  name: "", description: "",
  share_scope: "private",
  config: { dataset: "tickets", filters: [], group_by: [], measure: { key: "count" }, viz: "bar", sort: { direction: "desc" }, limit: 25 },
});

// =============== Chart renderer (shared by viewer + builder preview) ===============
export function CustomReportChart({ result, comparison }) {
  if (!result) return null;
  const viz = result.viz;
  const data = result.data;
  const deltaMap = comparison?.delta_pct;

  if (viz === "single_stat") {
    return (
      <div className="text-center py-10">
        <div className="font-heading text-5xl font-bold text-indigo-700">{data?.value ?? "—"}</div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mt-2">{data?.label || "Value"}</div>
        {typeof deltaMap === "number" && (
          <div className="mt-3 flex justify-center" data-testid="compare-delta-stat">
            <CompareBadge pct={deltaMap} size="sm" showLabel label="vs previous period" />
          </div>
        )}
      </div>
    );
  }
  if (viz === "table") {
    if (!data?.length) return <div className="text-xs text-slate-400 py-6 text-center">No rows</div>;
    const cols = Object.keys(data[0]);
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="text-xs uppercase tracking-wider text-slate-500">
            {cols.map((c) => <TableHead key={c}>{c}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {data.map((r, i) => (
              <TableRow key={i} className="hover:bg-slate-50">
                {cols.map((c) => <TableCell key={c} className="text-xs">{String(r[c] ?? "—").slice(0, 80)}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
  if (viz === "pie") {
    const pieData = (data || []).map((r) => ({ name: r.key, value: r.value || 0 }));
    return (
      <ResponsiveContainer height={280}>
        <PieChart>
          <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} innerRadius={50} paddingAngle={2}>
            {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // bar / stacked_bar / line / area share the same primary axis
  const isStacked = viz === "stacked_bar";
  const series = result.series || (data?.[0] ? (data[0].value !== undefined ? ["value"] : Object.keys(data[0]).filter((k) => k !== "key")) : []);

  if (viz === "line" || viz === "area") {
    const Comp = viz === "line" ? LineChart : AreaChart;
    const Series = viz === "line" ? Line : Area;
    return (
      <ResponsiveContainer height={280}>
        <Comp data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="key" tick={{ fontSize: 10, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s, i) => (
            <Series key={s} type="monotone" dataKey={s} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.3} />
          ))}
        </Comp>
      </ResponsiveContainer>
    );
  }
  return (
    <>
      <ResponsiveContainer height={280}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="key" tick={{ fontSize: 10, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s, i) => (
            <Bar key={s} dataKey={s} stackId={isStacked ? "a" : undefined}
                 fill={COLORS[i % COLORS.length]} radius={[6, 6, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {deltaMap && typeof deltaMap === "object" && (
        <div className="flex flex-wrap gap-1.5 mt-2 px-2 pb-1" data-testid="compare-delta-strip">
          {(data || []).slice(0, 12).map((r) => {
            const pct = deltaMap[String(r.key)];
            if (pct === null || pct === undefined) return null;
            return (
              <span key={String(r.key)} className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                <span className="font-medium text-slate-700 truncate max-w-[120px]">{String(r.key)}</span>
                <CompareBadge pct={pct} />
              </span>
            );
          })}
        </div>
      )}
    </>
  );
}

// =============== Main page ===============
export default function CustomReportsTab() {
  const [catalog, setCatalog] = useState(null);
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [compareMode, setCompareMode] = useState("mirror"); // "mirror" | "override"
  const [overrideFrom, setOverrideFrom] = useState("");
  const [overrideTo, setOverrideTo] = useState("");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [cat, list] = await Promise.all([
      api.get("/custom-reports/catalog"),
      api.get("/custom-reports"),
    ]);
    setCatalog(cat.data);
    setReports(list.data);
  };
  useEffect(() => { load(); }, []);

  const runReport = async (r, withComparison = false, mode = compareMode, from = overrideFrom, to = overrideTo) => {
    setSelected(r);
    setSelectedResult(null);
    setComparison(null);
    setShowCompare(withComparison);
    try {
      if (withComparison) {
        const body = { config: r.config, comparison_mode: mode };
        if (mode === "override") {
          if (!from || !to) {
            toast.error("Pick both From and To dates for the custom window.");
            return;
          }
          body.override_from = from;
          body.override_to = to;
        }
        const { data } = await api.post(`/custom-reports/query-with-comparison`, body);
        setSelectedResult(data.current);
        setComparison(data);
        if (data.comparison_window === null && mode === "mirror") {
          toast.message("No comparison available — add a date filter or switch to Custom window.");
        }
      } else {
        const { data } = await api.post(`/custom-reports/${r.id}/run`);
        setSelectedResult(data.result);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
  };

  const toggleCompare = async () => {
    if (!selected) return;
    await runReport(selected, !showCompare);
  };

  const applyCompareMode = async (mode) => {
    setCompareMode(mode);
    if (showCompare && selected) await runReport(selected, true, mode, overrideFrom, overrideTo);
  };

  const applyOverrideWindow = async () => {
    if (!selected) return;
    await runReport(selected, true, "override", overrideFrom, overrideTo);
  };

  const startCreate = () => { setEditing(blank()); setBuilderOpen(true); };
  const startEdit = (r) => {
    setEditing({
      ...r,
      // Strip stored metadata; only the editable fields
      name: r.name, description: r.description || "",
      share_scope: r.share_scope, config: r.config,
      _id_for_edit: r.id,
    });
    setBuilderOpen(true);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await api.delete(`/custom-reports/${r.id}`);
      toast.success("Deleted");
      if (selected?.id === r.id) { setSelected(null); setSelectedResult(null); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!catalog) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="custom-reports-tab">
      <Card className="border-indigo-100 bg-indigo-50/30">
        <CardContent className="p-4 flex items-start gap-3 text-xs text-indigo-900">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <strong>Custom Reports.</strong> Build your own analytics views from Tickets, Email log, or Audit log — pick filters, group by any field, choose a measure and chart type, and save.
            {catalog.can_create
              ? <> You can <strong>create new reports</strong> because you're a manager+.</>
              : <> Only managers and above can author new reports. You can run and view ones shared with you.</>}
          </div>
          {catalog.can_create && (
            <Button size="sm" onClick={startCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="custom-reports-new-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New report
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Saved reports list */}
        <Card className="border-slate-200 h-fit" data-testid="custom-reports-list">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Saved reports</CardTitle></CardHeader>
          <CardContent className="p-0">
            {reports.length === 0 ? (
              <div className="text-xs text-slate-400 px-4 py-6 text-center">No saved reports yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {reports.map((r) => {
                  const Scope = SCOPE_META[r.share_scope] || SCOPE_META.private;
                  const isSel = selected?.id === r.id;
                  return (
                    <div key={r.id} className={`px-3 py-2 hover:bg-slate-50 group ${isSel ? "bg-indigo-50/60" : ""}`} data-testid={`custom-report-${r.id}`}>
                      <button type="button" onClick={() => runReport(r)} className="w-full text-left">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium text-slate-800 truncate">{r.name}</div>
                          <Badge variant="outline" className={`text-[9px] ${Scope.tint}`}>
                            <Scope.Icon className="w-2.5 h-2.5 mr-0.5" />{Scope.label}
                          </Badge>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                          {catalog.datasets[r.config.dataset]?.label || r.config.dataset} · {r.config.viz}
                        </div>
                      </button>
                      <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => startEdit(r)} data-testid={`custom-report-edit-${r.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] text-rose-600" onClick={() => remove(r)} data-testid={`custom-report-delete-${r.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result pane */}
        <Card className="border-slate-200" data-testid="custom-reports-viewer">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <CardTitle className="font-heading text-base">
                  {selected ? selected.name : "Pick a saved report"}
                </CardTitle>
                {selected?.description && (
                  <p className="text-xs text-slate-500 mt-1">{selected.description}</p>
                )}
              </div>
              {selected && (
                <Button
                  variant={showCompare ? "default" : "outline"}
                  size="sm"
                  onClick={toggleCompare}
                  className={`h-7 text-[11px] ${showCompare ? "bg-indigo-600 hover:bg-indigo-700" : ""}`}
                  data-testid="compare-toggle">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {showCompare ? "Comparison on" : "Compare vs previous"}
                </Button>
              )}
            </div>
            {showCompare && comparison && comparison.comparison_window === null && compareMode === "mirror" && (
              <p className="text-[10px] text-amber-600 mt-1.5">
                Add a date filter to enable comparison — or switch to <strong>Custom window</strong>.
              </p>
            )}
            {showCompare && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid="compare-window-picker">
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                  <button type="button"
                          onClick={() => applyCompareMode("mirror")}
                          className={`px-2 py-1 text-[10px] ${compareMode === "mirror" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                          data-testid="compare-mode-mirror">
                    Previous period
                  </button>
                  <button type="button"
                          onClick={() => applyCompareMode("override")}
                          className={`px-2 py-1 text-[10px] ${compareMode === "override" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                          data-testid="compare-mode-override">
                    Custom window
                  </button>
                </div>
                {compareMode === "override" && (
                  <>
                    <Input type="date" value={overrideFrom} onChange={(e) => setOverrideFrom(e.target.value)}
                           className="h-7 text-[11px] w-36" data-testid="compare-override-from" />
                    <span className="text-slate-400">→</span>
                    <Input type="date" value={overrideTo} onChange={(e) => setOverrideTo(e.target.value)}
                           className="h-7 text-[11px] w-36" data-testid="compare-override-to" />
                    <Button size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={applyOverrideWindow}
                            disabled={!overrideFrom || !overrideTo}
                            data-testid="compare-override-apply">
                      Apply
                    </Button>
                  </>
                )}
                {comparison?.comparison_window && (
                  <span className="text-[10px] text-slate-500" data-testid="compare-window-label">
                    {comparison.comparison_window === "override"
                      ? `vs ${overrideFrom} → ${overrideTo}`
                      : "vs previous period"}
                  </span>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selected ? (
              <div className="text-xs text-slate-400 py-12 text-center">
                {catalog.can_create
                  ? "Click + New report above to author your first custom analytic, or pick a saved one on the left."
                  : "No reports shared with you yet."}
              </div>
            ) : !selectedResult ? (
              <div className="text-xs text-slate-400 py-8 text-center">Loading…</div>
            ) : (
              <CustomReportChart result={selectedResult} comparison={showCompare ? comparison : null} />
            )}
          </CardContent>
        </Card>
      </div>

      {builderOpen && (
        <BuilderDialog
          open={builderOpen}
          onOpenChange={setBuilderOpen}
          catalog={catalog}
          initial={editing}
          onSaved={() => { setBuilderOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// =============== Builder dialog ===============
function BuilderDialog({ open, onOpenChange, catalog, initial, onSaved }) {
  const [form, setForm] = useState(initial || blank());
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const ds = catalog.datasets[form.config.dataset];
  const fieldOptions = useMemo(() => Object.entries(ds?.fields || {}), [ds]);
  const measureOptions = useMemo(() => Object.entries(ds?.measures || {}), [ds]);

  const updateConfig = (patch) => setForm((f) => ({ ...f, config: { ...f.config, ...patch } }));
  const addFilter = () => updateConfig({ filters: [...form.config.filters, { field: fieldOptions[0]?.[0], op: "=", value: "" }] });
  const removeFilter = (i) => updateConfig({ filters: form.config.filters.filter((_, idx) => idx !== i) });
  const setFilter = (i, patch) => updateConfig({ filters: form.config.filters.map((f, idx) => idx === i ? { ...f, ...patch } : f) });

  const addGroupBy = () => updateConfig({ group_by: [...form.config.group_by, { field: fieldOptions[0]?.[0], granularity: "day" }] });
  const removeGroupBy = (i) => updateConfig({ group_by: form.config.group_by.filter((_, idx) => idx !== i) });
  const setGroupBy = (i, patch) => updateConfig({ group_by: form.config.group_by.map((g, idx) => idx === i ? { ...g, ...patch } : g) });

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/custom-reports/query", form.config);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || "",
        share_scope: form.share_scope,
        config: form.config,
      };
      if (form._id_for_edit) {
        await api.patch(`/custom-reports/${form._id_for_edit}`, payload);
        toast.success("Report updated");
      } else {
        await api.post("/custom-reports", payload);
        toast.success("Report created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="custom-report-builder">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            {form._id_for_edit ? "Edit" : "Build"} custom report
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick a dataset → add filters → group + aggregate → choose a chart → preview → save.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEFT: config */}
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs">Name <span className="text-rose-500">*</span></Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tickets by dept (last 30d)" data-testid="builder-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Share scope</Label>
                <Select value={form.share_scope} onValueChange={(v) => setForm({ ...form, share_scope: v })}>
                  <SelectTrigger className="text-xs" data-testid="builder-share-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private (only me)</SelectItem>
                    <SelectItem value="dept">Department</SelectItem>
                    <SelectItem value="org">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="text-xs" />
            </div>

            <div className="border-t pt-3 space-y-1.5">
              <Label className="text-xs">Dataset</Label>
              <Select
                value={form.config.dataset}
                onValueChange={(v) => updateConfig({ dataset: v, filters: [], group_by: [], measure: { key: "count" } })}
              >
                <SelectTrigger className="text-xs" data-testid="builder-dataset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(catalog.datasets).map(([k, d]) => <SelectItem key={k} value={k}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Filters */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Filters (AND)</Label>
                <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px]" onClick={addFilter} data-testid="builder-add-filter">
                  <Plus className="w-3 h-3 mr-0.5" />Add
                </Button>
              </div>
              {form.config.filters.length === 0 && <p className="text-[10px] text-slate-400 italic">No filters — entire dataset.</p>}
              {form.config.filters.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`builder-filter-${i}`}>
                  <Select value={f.field} onValueChange={(v) => setFilter(i, { field: v })}>
                    <SelectTrigger className="text-xs h-8 flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {fieldOptions.map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={f.op} onValueChange={(v) => setFilter(i, { op: v })}>
                    <SelectTrigger className="text-xs h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catalog.operators.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!["is_empty", "is_not_empty"].includes(f.op) && (
                    <Input value={f.value || ""} onChange={(e) => setFilter(i, { value: e.target.value })} className="text-xs h-8 w-36" placeholder="value" />
                  )}
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-1.5 text-rose-600" onClick={() => removeFilter(i)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Group-by */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Group by (1 or 2 dims)</Label>
                <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px]" onClick={addGroupBy} disabled={form.config.group_by.length >= 2} data-testid="builder-add-groupby">
                  <Plus className="w-3 h-3 mr-0.5" />Add
                </Button>
              </div>
              {form.config.group_by.length === 0 && <p className="text-[10px] text-slate-400 italic">No grouping — single aggregate.</p>}
              {form.config.group_by.map((g, i) => {
                const fmeta = ds?.fields?.[g.field] || {};
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <Select value={g.field} onValueChange={(v) => setGroupBy(i, { field: v })}>
                      <SelectTrigger className="text-xs h-8 flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {fieldOptions.map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {fmeta.type === "date" && (
                      <Select value={g.granularity || "day"} onValueChange={(v) => setGroupBy(i, { granularity: v })}>
                        <SelectTrigger className="text-xs h-8 w-24"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hour">Hour</SelectItem>
                          <SelectItem value="day">Day</SelectItem>
                          <SelectItem value="week">Week</SelectItem>
                          <SelectItem value="month">Month</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-1.5 text-rose-600" onClick={() => removeGroupBy(i)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Measure</Label>
                <Select value={form.config.measure.key} onValueChange={(v) => updateConfig({ measure: { key: v } })}>
                  <SelectTrigger className="text-xs" data-testid="builder-measure"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {measureOptions.map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Visualization</Label>
                <Select value={form.config.viz} onValueChange={(v) => updateConfig({ viz: v })}>
                  <SelectTrigger className="text-xs" data-testid="builder-viz"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {catalog.viz_types.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Limit</Label>
                <Input type="number" min={1} max={500} value={form.config.limit} onChange={(e) => updateConfig({ limit: parseInt(e.target.value || "25", 10) })} className="text-xs h-9" />
              </div>
            </div>
          </div>

          {/* RIGHT: preview */}
          <div className="space-y-2 lg:border-l lg:pl-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Live preview</Label>
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={runPreview} disabled={previewing} data-testid="builder-preview-btn">
                <Play className="w-3 h-3 mr-1" />{previewing ? "Running…" : "Run preview"}
              </Button>
            </div>
            <div className="border border-slate-100 rounded-md min-h-[300px] bg-white p-3">
              {!preview ? (
                <div className="text-xs text-slate-400 py-12 text-center">Click <strong>Run preview</strong> to see the chart.</div>
              ) : (
                <>
                  <CustomReportChart result={preview} />
                  <div className="text-[10px] text-slate-400 text-center mt-2">
                    {preview.total_rows} row(s) scanned · viz: <code>{preview.viz}</code>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="builder-save-btn">
            {saving ? "Saving…" : (form._id_for_edit ? "Save changes" : "Create report")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
